import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LoggingService } from '@app/core/services/logging/logging.service';
import { BookingRepository } from '@app/store/booking.repository';
import { LocationRepository } from '@app/store/location.repository';
import { environment } from '@env/environment';
import { TranslocoService } from '@jsverse/transloco';
import { Crisp } from 'crisp-sdk-web';
import {
  BehaviorSubject,
  firstValueFrom,
  Observable,
  Subscription,
} from 'rxjs';

import {
  buildBookingChatAttributes,
  buildEmptyBookingChatAttributes,
} from '../booking-attributes';
import { ChatIdentity, ChatProvider } from '../chat-provider';

/** How often the unread badge is refreshed — Crisp exposes a getter, not an event. */
const UNREAD_POLL_INTERVAL_MS = 5000;

/**
 * Crisp validates every session-data key against `/^([A-Za-z0-9_\-]{1,50})$/`
 * and rejects the *whole* payload with "Invalid data" if one key fails — so
 * the human-readable names Intercom uses ("Booking Id", "Check-in date") are
 * not usable as-is.
 *
 * The mapping is explicit rather than auto-slugified so the backend can rely
 * on these exact names when it reads the conversation metas, and so renaming
 * an Intercom attribute cannot silently change the Crisp contract.
 */
const CRISP_DATA_KEYS: Record<string, string> = {
  app_type: 'app_type',
  'Booking Id': 'booking_id',
  Property: 'property_name',
  'Property address': 'property_address',
  Floor: 'floor',
  Unit: 'unit',
  'Check-in date': 'checkin_date',
  'Check-out date': 'checkout_date',
  'Stay type': 'stay_type',
  'Property Manager URL': 'property_manager_url',
};

/** Fallback for attributes added to the shared builder without a mapping. */
function toCrispKey(attribute: string): string {
  return (
    CRISP_DATA_KEYS[attribute] ??
    attribute
      .trim()
      .replace(/[^\w-]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 50)
      .toLowerCase()
  );
}

@Injectable({
  providedIn: 'root',
})
export class CrispService implements ChatProvider {
  private unreadCountSubject = new BehaviorSubject<number>(0);
  private isInitialized = false;
  /** True once `Crisp.configure()` has run — see `revealChat()`. */
  private isConfigured = false;
  /** Firebase uid of the signed-in guest — sent as `user_id` in the chat context. */
  private guestUserId: string | undefined;
  /** Composer text waiting for the chatbox to mount — see `newMessage`. */
  private pendingMessageText: string | null = null;
  private initializationPromise: Promise<void> | null = null;
  private langSubscription: Subscription | null = null;
  private unreadPollHandle: ReturnType<typeof setInterval> | null = null;

  get unreadCount$(): Observable<number> {
    return this.unreadCountSubject.asObservable();
  }

  constructor(
    private readonly _http: HttpClient,
    private readonly _bookingRepository: BookingRepository,
    private readonly _locationRepository: LocationRepository,
    private readonly _translocoService: TranslocoService,
  ) {}

  async initialize(user: any, identity?: ChatIdentity): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    // Guard against concurrent initialization, same as the Intercom provider.
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this.doInitialize(user, identity);
    try {
      await this.initializationPromise;
    } finally {
      this.initializationPromise = null;
    }
  }

  private async doInitialize(
    user: any,
    identity?: ChatIdentity,
  ): Promise<void> {
    const websiteId = environment.crisp?.websiteId;
    if (!websiteId) {
      LoggingService.warn(
        '[Crisp] environment.crisp.websiteId is empty — skipping initialization',
      );
      return;
    }

    try {
      // Configure and load FIRST, synchronously, before any await.
      //
      // `tokenId` binds the chat session to our user id so the guest keeps
      // their history across reloads and devices, and it only takes effect
      // when passed to `configure()` — setting it after `load()` does not
      // rebind the session.
      //
      // Crucially, this must not sit behind the identity request: any chat
      // method called while Crisp is unconfigured makes the SDK bootstrap
      // itself (`createSingletonIfNecessary` + `autoInjectIfNecessary`) with
      // no websiteId and no tokenId, producing a throwaway cookie session.
      // Messages sent into it land in a different conversation than the one
      // the token later resolves to, so they appear to vanish on reload.
      //
      // `user.uid` is available synchronously and is the same value the
      // backend returns as `identity.userId` (both are the Firebase id).
      this.guestUserId = user?.uid || undefined;

      Crisp.configure(websiteId, {
        autoload: false,
        tokenId: this.guestUserId,
        locale: this._translocoService.getActiveLang() || 'en',
        safeMode: true,
      });

      this.isConfigured = true;

      Crisp.load();

      // Parity with Intercom's `hide_default_launcher: true` — the app drives
      // the chat from its own buttons.
      Crisp.chat.hide();

      // Identity only decorates the session (email, name, signature), so it
      // can resolve after the widget is already usable.
      const actualIdentity = identity ?? (await this.getIdentitySafely());

      const email = actualIdentity?.email || user?.email;
      if (email) {
        // The second argument is the HMAC-SHA256 signature from our backend.
        // Without it Crisp still accepts the email but marks the contact as
        // unverified, so only pass it when the backend actually signed one.
        Crisp.user.setEmail(email, actualIdentity?.hmac ?? undefined);
      }

      const nickname =
        [actualIdentity?.firstName, actualIdentity?.lastName]
          .filter(Boolean)
          .join(' ')
          .trim() || user?.displayName;
      if (nickname) {
        Crisp.user.setNickname(nickname);
      }

      this.isInitialized = true;
      LoggingService.log('[Crisp] Successfully initialized');

      this.teardownSubscriptions();
      this.watchLanguage();
      this.startUnreadPolling();
    } catch (error) {
      LoggingService.error('[Crisp] Initialization failed', error);
    }
  }

  /**
   * Identity verification is optional: if the backend has no `/crisp/identity`
   * endpoint yet, the widget still works — the contact is just unverified.
   * That keeps the provider switch usable before the backend lands.
   */
  private async getIdentitySafely(): Promise<ChatIdentity | undefined> {
    try {
      const url = `${environment.api.serverUrl}/crisp/identity`;
      return await firstValueFrom(this._http.get<ChatIdentity>(url));
    } catch (error) {
      LoggingService.warn(
        '[Crisp] Could not fetch identity — continuing unverified',
        error,
      );
      return undefined;
    }
  }

  private watchLanguage(): void {
    this.langSubscription = this._translocoService.langChanges$.subscribe(
      (lang) => {
        // `crisp-sdk-web` has no `setLocale`; the underlying `$crisp` queue
        // does. The global is declared by the package's own typings.
        try {
          $crisp.push(['config', 'locale', [lang || 'en']]);
        } catch (error) {
          LoggingService.error('[Crisp] Locale update failed', error);
        }
      },
    );
  }

  /**
   * Attaches the guest's booking context to the conversation.
   *
   * Called right before the chat is opened rather than on every booking
   * change, because the context belongs to the message the guest is about to
   * send. Pushing it eagerly at startup would also create a Crisp contact for
   * guests who never write, burning the plan's Customer Profiles quota.
   *
   * Keys are translated to Crisp's restricted format (see `CRISP_DATA_KEYS`).
   * Empty values are dropped as well — not because Crisp rejects them, but
   * because an absent key reads better in the inbox sidebar than a blank one.
   */
  private pushBookingContext(): void {
    if (!this.isConfigured) {
      return;
    }

    const activeBooking = this._bookingRepository.activeBooking;
    const attributes = activeBooking
      ? buildBookingChatAttributes(activeBooking)
      : buildEmptyBookingChatAttributes(
          this._locationRepository.activeLocation,
        );

    const populated: Record<string, string> = Object.fromEntries(
      Object.entries(attributes)
        .filter(([, value]) => value !== '')
        .map(([key, value]) => [toCrispKey(key), value]),
    );

    // `user_id` and `locale` are not part of the Intercom attribute payload
    // (Intercom carries them on the contact record instead), but agent-ai's
    // guest context reads both, so the Crisp provider supplies them here.
    if (this.guestUserId) {
      populated['user_id'] = this.guestUserId;
    }
    const locale = this._translocoService.getActiveLang();
    if (locale) {
      populated['locale'] = locale;
    }

    if (Object.keys(populated).length === 0) {
      return;
    }

    LoggingService.log(
      '[Crisp] Attaching booking context',
      populated['booking_id'] || '(no booking)',
    );

    try {
      Crisp.session.setData(populated);
    } catch (error) {
      LoggingService.error('[Crisp] Session data update failed', error);
    }
  }

  private startUnreadPolling(): void {
    this.refreshUnreadCount();
    this.unreadPollHandle = setInterval(
      () => this.refreshUnreadCount(),
      UNREAD_POLL_INTERVAL_MS,
    );

    // Event-driven refresh on top of the poll so the badge reacts immediately
    // to the common cases instead of waiting out the interval.
    Crisp.message.onMessageReceived(() => this.refreshUnreadCount());
    // One handler only: registering `onChatOpened` twice replaces the first.
    Crisp.chat.onChatOpened(() => {
      this.refreshUnreadCount();
      this.flushPendingMessageText();
    });
    Crisp.chat.onChatClosed(() => {
      this.refreshUnreadCount();
      // Restore the "no floating launcher" look the app has with Intercom.
      Crisp.chat.hide();
    });
  }

  private refreshUnreadCount(): void {
    try {
      const count = Crisp.chat.unreadCount() ?? 0;
      if (count !== this.unreadCountSubject.value) {
        this.unreadCountSubject.next(count);
      }
    } catch {
      // The widget may not be loaded yet; the next tick will pick it up.
    }
  }

  private teardownSubscriptions(): void {
    this.langSubscription?.unsubscribe();
    this.langSubscription = null;
    if (this.unreadPollHandle) {
      clearInterval(this.unreadPollHandle);
      this.unreadPollHandle = null;
    }
  }

  /**
   * Reveals and opens the chatbox.
   *
   * Crisp's `hide()` takes the entire widget out of the page, so `open()`
   * alone is a no-op while hidden — unlike Intercom, where
   * `hide_default_launcher` only removes the floating button and `present()`
   * still works. Every entry point therefore has to `show()` first.
   */
  private revealChat(): void {
    // Touching the SDK before `configure()` makes it bootstrap a throwaway
    // session with no websiteId and no tokenId; anything the guest writes
    // there is stranded in a conversation the token never resolves to.
    // Dropping the tap is recoverable, an orphaned conversation is not.
    if (!this.isConfigured) {
      LoggingService.warn(
        '[Crisp] Chat requested before initialization — ignoring',
      );
      return;
    }

    Crisp.chat.show();
    Crisp.chat.open();
  }

  async showMessages(): Promise<void> {
    this.pushBookingContext();
    this.revealChat();
  }

  async displayMessenger(): Promise<void> {
    this.pushBookingContext();
    this.revealChat();
  }

  /**
   * Opens the composer, optionally pre-filled.
   *
   * The order matters and is the opposite of Intercom's. Crisp's
   * `setMessageText` looks the composer up in the DOM and silently does
   * nothing when it is not there — so filling the text before revealing the
   * chatbox drops it every time. The text is therefore queued and written
   * once the chatbox is actually open.
   */
  async newMessage(message?: string): Promise<void> {
    this.pushBookingContext();
    this.pendingMessageText = message?.trim() || null;
    this.revealChat();
    this.flushPendingMessageText();
  }

  /**
   * Writes the queued composer text if the chatbox is already open; otherwise
   * leaves it for the `onChatOpened` handler in `startUnreadPolling`.
   */
  private flushPendingMessageText(): void {
    if (!this.pendingMessageText) {
      return;
    }

    try {
      if (!Crisp.chat.isChatOpened()) {
        return;
      }
      Crisp.message.setMessageText(this.pendingMessageText);
      this.pendingMessageText = null;
    } catch (error) {
      LoggingService.error('[Crisp] Could not pre-fill the composer', error);
    }
  }

  async helpCenter(): Promise<void> {
    Crisp.chat.setHelpdeskView();
    this.revealChat();
  }

  /**
   * Crisp addresses articles by `locale` + `slug`, not by the numeric id
   * Intercom uses. Callers pass whatever the active provider expects, so the
   * article references in `environment` have to be swapped alongside the
   * provider — an Intercom id will not resolve here.
   */
  async showArticle(articleRef: string): Promise<void> {
    const locale = this._translocoService.getActiveLang() || 'en';
    Crisp.chat.openHelpdeskArticle(locale, articleRef);
    Crisp.chat.open();
  }

  /**
   * No-op by decision, not by limitation.
   *
   * On Intercom these events feed Workflows configured in its dashboard, and
   * that automation has not been audited or rebuilt yet — see the pending
   * review of the `reservation-*` events. Crisp has no equivalent engine:
   * `session.pushEvent` only writes a timeline entry nothing reacts to, so
   * forwarding them would add noise and imply an automation that does not
   * exist. Crisp also rejects the whole event when any value is not a string,
   * number or boolean, which used to surface as an error dialog mid-checkout.
   *
   * Revisit once the Intercom Workflows are inventoried and we know which
   * events actually drive behaviour.
   */
  async trackEvent(
    eventName: string,
    _metaData?: Record<string, unknown>,
  ): Promise<void> {
    LoggingService.log(
      `[Crisp] Event "${eventName}" not forwarded (events disabled)`,
    );
  }

  /**
   * On Intercom this only logs a `report-a-problem` event — no guest-visible
   * effect — so with events disabled it is a no-op here too.
   */
  async reportAnIssue(): Promise<void> {
    await this.trackEvent('report-a-problem');
  }

  /**
   * No-op: Crisp has no client-triggered surveys. Its CSAT ("conversation
   * ratings", Essentials and up) is sent automatically when a conversation is
   * resolved and cannot be launched on demand.
   */
  startSurvey(surveyId: string): void {
    LoggingService.warn(
      `[Crisp] startSurvey('${surveyId}') ignored — Crisp has no client-triggered surveys`,
    );
  }

  async shutdown(): Promise<void> {
    this.teardownSubscriptions();
    try {
      Crisp.session.reset();
    } catch (error) {
      LoggingService.error('[Crisp] Session reset failed', error);
    }
    this.isInitialized = false;
    this.isConfigured = false;
    this.guestUserId = undefined;
    this.initializationPromise = null;
    this.unreadCountSubject.next(0);
  }
}
